if (!window.SatellaiPetParentsLoaded) {
  window.SatellaiPetParentsLoaded = true;

  class SatellaiPetParents {
    constructor(root) {
      this.root = root;
      this.cards = Array.from(root.querySelectorAll('[data-pet-parents-product-card]'));
      this.signupForms = Array.from(root.querySelectorAll('[data-pet-parents-signup-form]'));
      this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
      this.init();
    }

    init() {
      this.cards.forEach((card) => {
        this.bindProductGallery(card);
        this.bindVariantSelect(card);
        this.bindQuantityControls(card);
        this.bindAddToCart(card);
      });
      this.signupForms.forEach((form) => this.bindSignupForm(form));
    }

    bindProductGallery(card) {
      const image = card.querySelector('[data-product-image]');
      const thumbs = Array.from(card.querySelectorAll('[data-product-gallery-thumb]'));

      if (!image || thumbs.length === 0) return;

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const nextImage = thumb.dataset.image;
          if (!nextImage) return;

          image.src = nextImage;
          image.alt = thumb.dataset.imageAlt || image.alt;

          thumbs.forEach((item) => {
            const isActive = item === thumb;
            item.classList.toggle('is-active', isActive);

            if (isActive) {
              item.setAttribute('aria-current', 'true');
            } else {
              item.removeAttribute('aria-current');
            }
          });
        });
      });
    }

    normalizeImageUrl(url) {
      if (!url) return '';

      const parser = document.createElement('a');
      parser.href = url;
      return parser.href.replace(/^https?:/, '');
    }

    normalizeImageToken(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/&quot;|&#34;/g, ' ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    getOptionImageTokens(option) {
      if (!option) return [];

      const stopwords = new Set([
        'free',
        'month',
        'months',
        'plan',
        'year',
        'years',
        'pack',
        'membership',
        'included'
      ]);
      const values = [
        option.dataset.option0,
        option.dataset.option1,
        option.dataset.option2,
        option.dataset.optionLabel,
        option.textContent
      ];

      return Array.from(new Set(values
        .flatMap((value) => this.normalizeImageToken(value).split(' '))
        .filter((token) => token.length >= 3 && !/^\d+$/.test(token) && !stopwords.has(token))));
    }

    findGalleryThumbForOption(card, option) {
      const thumbs = Array.from(card.querySelectorAll('[data-product-gallery-thumb]'));
      if (thumbs.length === 0) return null;

      const tokens = this.getOptionImageTokens(option);
      if (tokens.length === 0) return null;

      let bestThumb = null;
      let bestScore = 0;

      thumbs.forEach((thumb) => {
        const haystack = this.normalizeImageToken(`${thumb.dataset.image || ''} ${thumb.dataset.imageAlt || ''}`);
        const score = tokens.reduce((total, token) => total + (haystack.includes(token) ? 1 : 0), 0);

        if (score > bestScore) {
          bestScore = score;
          bestThumb = thumb;
        }
      });

      return bestThumb;
    }

    syncProductImage(card, option) {
      let nextImage = option ? option.dataset.variantImage : '';

      const image = card.querySelector('[data-product-image]');
      if (!image) return;

      const thumbs = Array.from(card.querySelectorAll('[data-product-gallery-thumb]'));
      let activeThumb = null;

      if (!nextImage) {
        activeThumb = this.findGalleryThumbForOption(card, option);
        nextImage = activeThumb ? activeThumb.dataset.image : '';
      }

      if (!nextImage) return;

      image.src = nextImage;
      image.alt = option.dataset.variantImageAlt || (activeThumb ? activeThumb.dataset.imageAlt : '') || option.dataset.optionLabel || image.alt;

      const normalizedImage = this.normalizeImageUrl(nextImage);
      if (thumbs.length === 0) return;

      activeThumb = activeThumb || thumbs.find((thumb) => this.normalizeImageUrl(thumb.dataset.image) === normalizedImage);

      thumbs.forEach((thumb) => {
        const isActive = thumb === activeThumb;
        thumb.classList.toggle('is-active', isActive);

        if (isActive) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    bindVariantSelect(card) {
      const select = card.querySelector('[data-variant-select]');
      if (!select) return;

      const optionSelects = Array.from(card.querySelectorAll('[data-product-option-select]'));
      const variantOptions = Array.from(select.options);
      const getOptionValue = (option, index) => option ? option.getAttribute(`data-option-${index}`) || '' : '';

      const optionMatches = (option, selectedValues) => selectedValues.every((value, index) => {
        if (!value) return true;
        return getOptionValue(option, index) === value;
      });

      optionSelects.forEach((optionSelect) => {
        optionSelect.addEventListener('change', () => {
          const currentOption = select.options[select.selectedIndex];
          const selectedValues = [0, 1, 2].map((index) => getOptionValue(currentOption, index));

          optionSelects.forEach((visibleSelect) => {
            const index = parseInt(visibleSelect.dataset.optionIndex, 10);
            if (Number.isFinite(index)) {
              selectedValues[index] = visibleSelect.value;
            }
          });

          const nextOption = variantOptions.find((option) => !option.disabled && optionMatches(option, selectedValues))
            || variantOptions.find((option) => {
              if (option.disabled) return false;

              return optionSelects.every((visibleSelect) => {
                const index = parseInt(visibleSelect.dataset.optionIndex, 10);
                return !Number.isFinite(index) || getOptionValue(option, index) === visibleSelect.value;
              });
            });

          if (nextOption) {
            select.value = nextOption.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });

      const updateFromSelect = (shouldSyncImage = true) => {
        const option = select.options[select.selectedIndex];
        if (!option) return;

        const price = card.querySelector('[data-product-price]');
        const comparePrice = card.querySelector('[data-product-compare-price]');
        const cardPrice = card.querySelector('[data-product-card-price]');
        const cardComparePrice = card.querySelector('[data-product-card-compare-price]');
        const optionLabel = card.querySelector('[data-product-option-label]');
        const quantityInput = card.querySelector('[data-quantity-input]');
        const addButton = card.querySelector('[data-add-to-cart]');
        const quantity = this.normalizeQuantity(quantityInput ? quantityInput.value : 1);
        const priceCents = parseInt(option.dataset.priceCents, 10);
        const comparePriceCents = parseInt(option.dataset.comparePriceCents, 10);
        const visibleLabels = [];

        optionSelects.forEach((optionSelect) => {
          const index = parseInt(optionSelect.dataset.optionIndex, 10);
          if (!Number.isFinite(index)) return;

          const value = getOptionValue(option, index);
          if (value) {
            optionSelect.value = value;
            visibleLabels.push(value);
          }
        });

        if (optionLabel) {
          optionLabel.textContent = visibleLabels.length > 0
            ? visibleLabels.join(' / ')
            : option.dataset.optionLabel || option.textContent.trim();
        }

        if (shouldSyncImage) {
          this.syncProductImage(card, option);
        }

        if (addButton) {
          addButton.disabled = option.disabled || !option.value;
        }

        if (!option.dataset.price && !option.dataset.priceCents) {
          return;
        }

        if (cardPrice) cardPrice.textContent = option.dataset.price || '';

        if (cardComparePrice) {
          cardComparePrice.textContent = option.dataset.comparePrice || '';
          cardComparePrice.hidden = !option.dataset.comparePrice;
        }

        if (price) {
          price.textContent = Number.isFinite(priceCents)
            ? this.formatMoney(priceCents * quantity)
            : option.dataset.price || '';
        }

        if (comparePrice) {
          comparePrice.textContent = Number.isFinite(comparePriceCents)
            ? this.formatMoney(comparePriceCents * quantity)
            : option.dataset.comparePrice || '';
          comparePrice.hidden = !option.dataset.comparePrice;
        }
      };

      select.addEventListener('change', (event) => {
        updateFromSelect(!event.detail?.skipVariantImage);
      });
      updateFromSelect();
    }

    normalizeQuantity(value) {
      const parsed = parseInt(value, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    bindQuantityControls(card) {
      const input = card.querySelector('[data-quantity-input]');
      const minus = card.querySelector('[data-quantity-minus]');
      const plus = card.querySelector('[data-quantity-plus]');
      const select = card.querySelector('[data-variant-select]');
      const display = card.querySelector('[data-quantity-display]');

      if (!input) return;

      const syncPrice = () => {
        input.value = this.normalizeQuantity(input.value);
        if (display) display.textContent = input.value;
        if (select) {
          select.dispatchEvent(new CustomEvent('change', {
            detail: {
              skipVariantImage: true
            }
          }));
        }
      };

      if (minus) {
        minus.addEventListener('click', () => {
          input.value = Math.max(1, this.normalizeQuantity(input.value) - 1);
          syncPrice();
        });
      }

      if (plus) {
        plus.addEventListener('click', () => {
          input.value = this.normalizeQuantity(input.value) + 1;
          syncPrice();
        });
      }

      input.addEventListener('change', () => {
        input.value = this.normalizeQuantity(input.value);
        syncPrice();
      });
    }

    bindAddToCart(card) {
      const button = card.querySelector('[data-add-to-cart]');
      const select = card.querySelector('[data-variant-select]');
      const input = card.querySelector('[data-quantity-input]');

      if (!button || !select || !input) return;

      button.addEventListener('click', async () => {
        if (button.disabled) return;

        const variantId = select.value;
        const selectedOption = select.options[select.selectedIndex];
        const planVariantId = selectedOption ? selectedOption.dataset.planVariantId : '';
        const quantity = Math.max(1, parseInt(input.value, 10) || 1);
        const originalLabel = button.textContent;

        if (!variantId) return;

        button.disabled = true;
        button.textContent = button.dataset.loadingLabel || 'Adding...';

        try {
          const item = {
            id: variantId,
            quantity: quantity
          };

          if (planVariantId) {
            item.properties = {
              _plan_variant_id: String(planVariantId)
            };
          }

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              items: [item]
            })
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.description || 'Failed to add to cart');
          }

          document.dispatchEvent(new CustomEvent('gtm:product-added', {
            detail: {
              response: data,
              variantId: variantId,
              quantity: quantity,
              button: button
            }
          }));

          if (window.Alpine && Alpine.store && Alpine.store('xMiniCart')) {
            if (typeof Alpine.store('xMiniCart').reLoad === 'function') {
              Alpine.store('xMiniCart').reLoad();
            }

            window.setTimeout(() => {
              if (typeof Alpine.store('xMiniCart').openCart === 'function') {
                Alpine.store('xMiniCart').openCart();
              }
            }, 300);
          }
        } catch (error) {
          console.error(error);
        } finally {
          button.disabled = false;
          button.textContent = originalLabel;
        }
      });
    }

    bindSignupForm(form) {
      const input = form.querySelector('input[name="contact[email]"]');
      const privacy = form.querySelector('[data-pet-parents-signup-privacy]');
      const button = form.querySelector('[data-pet-parents-signup-submit]');
      const message = form.querySelector('[data-pet-parents-signup-message]');

      if (!input || !button) return;

      const originalLabel = button.textContent;
      const submitUrl = form.dataset.submitUrl || form.getAttribute('action') || '/contact';
      const successMessage = form.dataset.successMessage || 'Thanks for joining. We will keep you posted.';
      const errorMessage = form.dataset.errorMessage || 'Something went wrong. Please try again.';
      const invalidMessage = form.dataset.invalidMessage || 'Please enter a valid email and accept the Privacy Policy.';
      const rateLimitMessage = 'Too many requests. Please try again later.';

      const showMessage = (type, text) => {
        if (!message) return;

        message.hidden = false;
        message.textContent = text;
        message.setAttribute('role', type === 'error' ? 'alert' : 'status');
        message.dataset.state = type;
      };

      const hideMessage = () => {
        if (!message) return;

        message.hidden = true;
        message.textContent = '';
        message.removeAttribute('data-state');
      };

      const setDisabled = (isDisabled) => {
        input.disabled = isDisabled;
        if (privacy) privacy.disabled = isDisabled;
        button.disabled = isDisabled;
        button.textContent = isDisabled
          ? button.dataset.loadingLabel || 'Submitting...'
          : originalLabel;
      };

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        hideMessage();

        if (!form.checkValidity()) {
          if (typeof form.reportValidity === 'function') {
            form.reportValidity();
          }
          showMessage('error', invalidMessage);
          return;
        }

        const formData = new FormData(form);

        if (!formData.has('form_type')) {
          formData.append('form_type', 'customer');
        }

        if (!formData.has('utf8')) {
          formData.append('utf8', '✓');
        }

        if (!formData.has('contact[accepts_marketing]')) {
          formData.append('contact[accepts_marketing]', 'true');
        }

        setDisabled(true);

        try {
          const response = await fetch(submitUrl, {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          });

          if (!response.ok) {
            showMessage('error', response.status === 429 ? rateLimitMessage : errorMessage);
            return;
          }

          showMessage('success', successMessage);
          input.value = '';
          if (privacy) privacy.checked = false;
        } catch (error) {
          showMessage('error', errorMessage);
        } finally {
          setDisabled(false);
        }
      });
    }

    formatMoney(cents) {
      const format = this.moneyFormat;
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = format.match(placeholderRegex);

      if (!match) {
        return `$${(cents / 100).toFixed(2)}`;
      }

      const token = match[1];
      let value = '';

      switch (token) {
        case 'amount':
          value = this.formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = this.formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = this.formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = this.formatWithDelimiters(cents, 0, '.', ',');
          break;
        default:
          value = this.formatWithDelimiters(cents, 2);
      }

      return format.replace(placeholderRegex, value).replace(/<[^>]*>/g, '');
    }

    formatWithDelimiters(cents, precision = 2, thousands = ',', decimal = '.') {
      if (!Number.isFinite(cents)) return '';

      const number = (cents / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsPart = parts[1] ? `${decimal}${parts[1]}` : '';

      return `${dollars}${centsPart}`;
    }
  }

  const initPetParents = (scope = document) => {
    scope.querySelectorAll('[data-pet-parents-section]').forEach((root) => {
      if (root.dataset.petParentsInitialized === 'true') return;
      root.dataset.petParentsInitialized = 'true';
      new SatellaiPetParents(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initPetParents());
  } else {
    initPetParents();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initPetParents(event.target);
  });
}
